/**
 * WidgetFormBlock — fetch + dispatch stanów dla klocka `widget_view` w mode form/preview
 * (Faza 23, Etap 3 T2 — host formularza kartoteki).
 *
 * Mirror `CustomWidget` (host pulpitu): fetch przez `useWidgetData`, zakres firmy 1:1 z
 * `useActiveCompanyScope` (jak tam — `companyScope` jest tylko cache-busterem w query key,
 * `X-Company-Id` dopina globalny interceptor), a czysty render WYNIKU deleguje do wspólnego
 * `WidgetResultView`. RÓŻNICA względem pulpitu: kontekst rekordu.
 *
 * `recordId` NIE przychodzi przez propsy — `RecursiveFormRenderer` przekazuje elementowi layoutu
 * TYLKO `{id, type, settings}` + `formData` (bez recordId, patrz `tryRenderLayoutElement` w tym
 * pliku wołający), więc ten komponent bierze go SAM z URL (`useParams`), tak jak strony detalu
 * (`ContractorDetail`/`CompanyDetail`/`EmployeeDetailKeyed`) robią to już dziś.
 *
 * Trasy detalu MAJĄ osobną ścieżkę `/…/new` (bez `:id`) obok `/…/:id` (patrz `TenantTabPanels.tsx`)
 * — na `/…/new` `useParams().id` jest `undefined`, NIE stringiem `'new'`. Sprawdzamy oba warianki
 * (`!recordId || recordId === 'new'`) żeby nie połknąć ewentualnej przyszłej trasy z dosłownym
 * segmentem `:id==='new'`.
 *
 * Bez rekordu (tworzenie nowej kartoteki) — notka „widoczny po zapisie", ZERO fetchu (`enabled`
 * na `useWidgetData` idzie na `false`, hook i tak trzeba wywołać bezwarunkowo — zasada hooków).
 *
 * Widoczność danych: widget dziedziczy widoczność listy źródła — to backend, front tylko wysyła
 * `recordId`/`entity` w query (patrz `useWidgetData`/`WidgetDataContext`), zero dodatkowej logiki
 * bezpieczeństwa tutaj (decyzja architektoniczna, nie zmieniać bez ADR).
 */

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { classifyDataError } from '@/components/dashboard/dashboard-api';
import { WidgetResultView } from '@/components/dashboard/widgets/WidgetResultView';
import {
  WidgetDataError,
  WidgetEmpty,
  WidgetLoading,
} from '@/components/dashboard/widgets/WidgetState';
import { useWidgetData } from '@/hooks/useWidgetDefinitions';
import { useActiveCompanyScope } from '@/lib/use-active-company';

export interface WidgetFormBlockProps {
  definitionId: string | null;
  entity: string | null;
}

export function WidgetFormBlock({ definitionId, entity }: WidgetFormBlockProps) {
  const { t } = useTranslation();
  const { id: recordId } = useParams<{ id: string }>();
  const { isAll, companyId } = useActiveCompanyScope();

  const hasRecord = !!recordId && recordId !== 'new';
  const shouldFetch = !!definitionId && hasRecord;

  const query = useWidgetData(
    definitionId ?? '',
    { recordId, entity: entity ?? undefined },
    // scope w kluczu: przełączenie firmy odświeża blok (X-Company-Id dopina interceptor)
    { enabled: shouldFetch, companyScope: isAll ? 'all' : companyId },
  );

  if (!definitionId) {
    return <WidgetEmpty message={t('tenant.widgets.host.missingDefinition')} />;
  }
  if (!hasRecord) {
    return <WidgetEmpty message={t('tenant.widgets.layoutElement.visibleAfterSave')} />;
  }
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError) {
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );
  }

  const result = query.data;
  if (!result) return <WidgetLoading />;
  return <WidgetResultView result={result} visualType={result.visualType} />;
}
